import { filterBy } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { FieldRenderPropsBase, SVGIcon } from "@progress/kendo-react-common";
import { FieldRenderProps, FieldWrapper } from "@progress/kendo-react-form";
import { Checkbox } from "@progress/kendo-react-inputs";
import { Error, Hint } from "@progress/kendo-react-labels";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { Popup } from "@progress/kendo-react-popup";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { AxiosError } from "axios";
import classNames from "classnames";
import compact from "lodash-es/compact";
import isEmpty from "lodash-es/isEmpty";
import isFunction from "lodash-es/isFunction";
import isString from "lodash-es/isString";
import noop from "lodash-es/noop";
import { X } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Else, If, Then } from "react-if";
import useDeepCompareEffect from "use-deep-compare-effect";
import { TlxHttpResponse } from "@/api";
import Errors from "@/components/Dialog/Error";
import { SelectData } from "@/components/Kendo/Form/FinalForm";
import FormLabel from "@/components/Kendo/Form/FormLabel";
import IconHint from "@/components/Kendo/Form/IconHint";
import ToolTipContent from "@/components/Kendo/ToolTip/ToolTipContent";
import getTextWidth from "@/helpers/getTextWidth";
import useInfiniteScroll from "@/hooks/useInfiniteScroll";
import useOnClickOutside from "@/hooks/useOnClickOutside";
import useVisibility from "@/hooks/useVisibility";
import useWindowSize from "@/hooks/useWindowSize";

import "./styles.scss";

// Chip-fit widths, measured against the rendered DOM. Round up, never down: an
// under-estimate overflows the chip row, which widens its grid track.
const CHIP_CHROME_PX = 52; // border + padding + remove button + margin (true 48)
const CHIP_ICON_PX = 27; // leading imageRender icon + its margin
const VALUES_INSET_PX = 30; // .k-input-values padding-inline
const ARROW_BUTTON_PX = 28; // .k-input-button, outside .k-input-values
const OVERFLOW_CHIP_PX = 50; // room for the trailing "+N"

/** MultiSelect always works with an array. Coerce leftover scalars/objects
 *  (e.g. an Equals value after switching the operator to In) so `.find`/`.map`
 *  never throw. */
export const toSelectDataArray = <T = SelectData,>(raw: unknown): T[] =>
  Array.isArray(raw) ? (raw as T[]) : [];

/** The shape MultiSelect actually invokes `onFilterChange` with (its own
 * bespoke event, not Kendo's `MultiSelectFilterChangeEvent`) — exported so
 * callers can type their handler against it instead of redeclaring it inline. */
export type MultiSelectFilterChangeEvent = { filter: { value: string } };

const customValueRenderInternal = (
  filterValue: string,
  addCustomValue: (text: string, target?: EventTarget | null) => void,
) => {
  return (
    <li
      role="option"
      className="k-list-item k-cursor-pointer"
      onClick={({ target }) => addCustomValue(filterValue.trim(), target)}
      style={{ padding: "var(--tlx-space-2) var(--tlx-space-4)" }}
    >
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <span style={{ fontWeight: 600 }}>{`Add "${filterValue.trim()}"`}</span>
      </div>
    </li>
  );
};

/**
 * A custom function to determine whether a given option in a multi-select component should be disabled
 * based on its relationship to other selected options.
 *
 * @param data - The option being checked, represented as a `SelectData` object.
 * @param value - The currently selected options, represented as an array of `SelectData` objects.
 * @returns `true` if the option should be disabled, otherwise `false`.
 *
 * The function handles four cases:
 * 1. If the option represents "ALL", it is disabled if there are any selected options.
 * 2. If the option represents a schema (contains a dot in its name), it is disabled if its corresponding database is already selected.
 * 3. If the option represents a database, it is disabled if there is any schema in the selected options that starts with the database name.
 * 4. If "ALL" is selected, all options apart from "ALL" are disabled.
 */
export const fnDatabaseWithSchemaCheckAllDisabled = (
  data: SelectData,
  value: SelectData[],
) => {
  const { name } = data;
  const isAll = name?.toLowerCase() === "all";

  // Case 4: If "ALL" is selected, disable all options apart from "ALL"
  const isAllSelected = value.some(item => item.name?.toLowerCase() === "all");
  if (isAllSelected && !isAll) {
    return true;
  }

  // Case 1: If the option represents "ALL", disable it if there are any selected options
  if (isAll && !isAllSelected) {
    return value.length > 0;
  }

  const isSchema = name?.includes(".");
  const database = name?.split(".")[0] as string;

  // Case 2: If the option is a schema, disable it if the database is already selected
  if (isSchema) {
    return value.some(
      item => item.name?.toLowerCase() === database.toLowerCase(),
    );
  } else {
    // Case 3: If the option is a database, disable it if there is any schema in the value that starts with the database name
    return value.some(item =>
      item.name?.toLowerCase().startsWith(name?.toLowerCase() + "."),
    );
  }
};

const MultiSelect: React.FC<
  Partial<FieldRenderProps> & {
    imageRender?(data: SelectData): React.ReactNode;
    customValueRender?(
      filterValue: string,
      addCustomValue: (text: string) => void,
    ): React.ReactNode;
    /**
     * Custom render for each selected value (chip), same pattern as FromDropDownListPagination.
     * Receives the default span element and the selected item; return the element as-is or clone/customize it.
     */
    valueRender?(
      element: React.ReactElement<HTMLSpanElement>,
      selectedValue: SelectData,
    ): React.ReactNode;
    displayNameKey?: string;
    svgIcon?: SVGIcon;
    hasSelectAll?: boolean;
    groupField?: string; // prop for grouping
    /** When true and groupField is set with multiple groups, show groups in tabs instead of sections */
    groupTabView?: boolean;
    apiError?: AxiosError<TlxHttpResponse>;
    isSingleSelect?: boolean;
    showAllSelectedItems?: boolean;
    fnCheckAllDisabled?: (data: SelectData, value: SelectData[]) => boolean; // custom function to check if an option should be disabled using current selected value and option data
    /** When provided, "Select all" selects only items that pass this filter (e.g. only ALL_SCHEMAS / ALL_TABLES). Omit to select every item. */
    fnSelectAllFilter?: (item: SelectData) => boolean;
    onLoadMore?: () => void;
    hasMore?: boolean;
    isLoadingMore?: boolean;
    optional?: boolean;
    /** Typed here (rather than arriving untyped via `Partial<FieldRenderProps>`)
     * so the handler signature can't drift from what `filterChange` actually
     * invokes it with. */
    onFilterChange?: (event: MultiSelectFilterChangeEvent) => void;
    /** When true, `data` is assumed to already be filtered server-side by the
     * typed filter text, so the internal client-side filterBy pass is skipped.
     * Pass it only while a server-side search is actually in flight/applied —
     * if the parent hasn't sent the term (e.g. below its minimum length), the
     * client pass is what still narrows the list. Callers that repoint `data`
     * per keystroke should also pass `setValue={false}`, otherwise the prune
     * effect clears selections absent from the current server page. */
    serverFiltering?: boolean;
  }
> = fieldRenderProps => {
  const {
    validationMessage,
    touched,
    id = fieldRenderProps.name || "label",
    label,
    valid,
    disabled,
    hint,
    data,
    // noDataMessage,
    floatingLabel,
    floatLabel,
    placeholder,
    dataItemKey = "id",
    hintIcon,
    style,
    popupWidth,
    loading = false,
    onChange,
    imageRender,
    itemRender,
    valueRender,
    displayNameKey,
    className = "",
    hidePopup = false,
    textField = "name",
    svgIcon,
    onFilterChange,
    allowCustom,
    hasSelectAll,
    groupField,
    groupTabView = false,
    apiError,
    fnCheckAllDisabled,
    fnSelectAllFilter,
    customValueRender = customValueRenderInternal,
    isSingleSelect = false,
    showAllSelectedItems = false,
    onLoadMore,
    hasMore,
    isLoadingMore,
    optional,
    serverFiltering = false,
    ...others
  } = fieldRenderProps;

  const { t } = useTranslation();
  const showValidationMessage = touched && validationMessage;
  const showApiError = !others.loading && apiError;
  const showHint = hint;
  const hindId = showHint ? `${id}_hint` : "";
  const errorId = showValidationMessage ? `${id}_error` : "";
  const labelId = label ? `${id}_label` : "";
  const [filterValue, setFilterValue] = React.useState<string>("");
  const [value, setValue] = React.useState<SelectData[]>(
    toSelectDataArray(fieldRenderProps.value),
  );
  const anchor = React.useRef<HTMLSpanElement | null>(null);
  const divRef = React.useRef<HTMLDivElement | null>(null);
  const [show, setShow] = React.useState(false);
  const { width } = useWindowSize();
  const [visibleItems, setVisibleItems] = useState<SelectData[]>(
    value.length > 0 ? [value[0]] : [],
  );
  const isVisible = useVisibility(anchor);
  useEffect(() => {
    setValue(toSelectDataArray(fieldRenderProps.value));
  }, [fieldRenderProps.value]);

  const checkAll = useCallback(
    (name: string) => name?.toLowerCase() === "all",
    [],
  );

  const newData = React.useMemo(() => {
    if (fnCheckAllDisabled) {
      return [...data].map((item: SelectData) => ({
        ...item,
        checkAllDisabled: fnCheckAllDisabled(item, value),
      }));
    }

    const hasAll =
      value.find((item: SelectData) => checkAll(item?.name ?? ""))?.name !==
      undefined;

    return [...data].map((item: SelectData) => ({
      ...item,
      checkAllDisabled: hasAll
        ? !checkAll(item.name ?? "")
        : value.length > 0
          ? checkAll(item.name ?? "")
          : false,
    }));
  }, [data, value, checkAll, fnCheckAllDisabled]);

  const hasAll = data.some((item: SelectData) => checkAll(item.name ?? ""));

  useDeepCompareEffect(() => {
    if (others.setValue === false) return;
    if (
      newData?.length > 0 &&
      !others.loading &&
      value.length > 0 &&
      !allowCustom
    ) {
      // removing selected options which are not present in the options
      const newValues = compact(
        value.map(item =>
          newData.find((dataItem: SelectData) => {
            const idAttr = dataItemKey as keyof SelectData;
            return dataItem[idAttr] === item[idAttr];
          }),
        ),
      );
      if (newValues.length) setValue(newValues);
      onChange?.({ value: newValues });
    }
  }, [newData, value, others.loading, others.setValue]);

  const filterChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value || "";
      if (onFilterChange) {
        onFilterChange({
          filter: {
            value,
          },
        });
      }
      setFilterValue(value);
    },
    [onFilterChange],
  );

  const dropDownData: SelectData[] = useMemo(() => {
    if (filterValue && !serverFiltering) {
      return filterBy([...newData], {
        logic: "or",
        filters: [
          {
            field: textField,
            operator: "contains",
            value: filterValue,
          },
        ],
      });
    }

    return newData;
  }, [newData, filterValue, textField, serverFiltering]);

  useOnClickOutside(divRef, () => setShow(false), anchor);

  // A boolean, not `imageRender` itself: call sites pass inline arrows, and a
  // new identity each render would re-run the effect below into a state loop.
  const hasChipIcon = Boolean(imageRender);

  const updateVisibleItems = useCallback(
    (selectValues: SelectData[]) => {
      if (
        !anchor.current ||
        !selectValues ||
        selectValues.length === 0 ||
        anchor.current.offsetWidth === 0
      ) {
        setVisibleItems([]);
        return;
      }
      if (showAllSelectedItems) {
        setVisibleItems(selectValues);
        return;
      }

      const containerWidth =
        anchor.current.offsetWidth - VALUES_INSET_PX - ARROW_BUTTON_PX;
      let currentWidth = 0;
      const newVisibleItems: SelectData[] = [];

      for (const item of selectValues) {
        // Measure what the chip renders: `displayNameKey` can be far longer
        // than `textField` (e.g. a fully-qualified object name).
        const text = (
          displayNameKey
            ? item[displayNameKey as "name"]
            : item[textField as "name"]
        )!;
        const itemWidth =
          getTextWidth(text) +
          CHIP_CHROME_PX +
          (hasChipIcon ? CHIP_ICON_PX : 0);
        if (currentWidth + itemWidth + OVERFLOW_CHIP_PX > containerWidth) break;
        newVisibleItems.push(item);
        currentWidth += itemWidth;
      }

      if (newVisibleItems.length === 0 && selectValues.length > 0) {
        setVisibleItems([selectValues[0]]);
      } else {
        setVisibleItems(newVisibleItems);
      }
    },
    [textField, displayNameKey, hasChipIcon, showAllSelectedItems],
  );

  useEffect(() => {
    updateVisibleItems(value);
  }, [value, isVisible, width, updateVisibleItems]);

  /**
   * hide popup when window is resizing
   */
  useEffect(() => {
    setShow(false);
  }, [width]);

  const onClick = () => {
    if (disabled) return;
    others.onClick?.();
    others.onBlur?.();
    setShow(!show);
  };

  const removeItem = useCallback(
    (item: SelectData) => {
      const values = value.filter(
        val => val[dataItemKey as "id"] !== item[dataItemKey as "id"],
      );
      setValue([...values]);
      onChange?.({ value: values });
    },
    [dataItemKey, onChange, value],
  );

  if (!data) return null;

  const onClearAll = () => {
    onChange?.({ value: [] });
  };

  const onSelectAll = () => {
    const toSelect = fnSelectAllFilter
      ? data.filter((item: SelectData) => fnSelectAllFilter(item))
      : [...data];
    setValue(toSelect);
    onChange?.({ value: toSelect });
  };

  const anchorWidth = anchor.current?.getBoundingClientRect().width || "auto";
  const rest = (value || []).length - (visibleItems || []).length;

  const computePopupWidth =
    loading || (!loading && data.length === 0)
      ? anchorWidth
      : popupWidth || anchorWidth;

  return (
    <FieldWrapper>
      <FormLabel
        id={labelId}
        editorId={id}
        editorValid={valid!}
        editorDisabled={disabled}
        floating={floatingLabel}
        floatLabel={floatLabel}
        label={label}
        value={others.value?.length ? others.value : null}
        placeholder={placeholder}
        optional={optional}
      >
        <span
          className={classNames(
            "k-dropdownlist k-picker k-picker-md k-rounded-md k-picker-solid",
            disabled && "k-disabled",
            className,
            showValidationMessage && "k-invalid",
            isSingleSelect && "k-picker-single-select",
          )}
          tabIndex={0}
          role="combobox"
          aria-required="false"
          aria-haspopup="listbox"
          aria-expanded={show}
          aria-owns={`${id}-listbox-id`}
          aria-labelledby={`${id}_label`}
          aria-describedby={`${hindId} ${errorId}`.trim() || undefined}
          aria-invalid={!!showValidationMessage || undefined}
          id={`${id}-input`}
          style={style}
          onClick={onClick}
          ref={anchor}
        >
          <div className="k-input-values" style={{ cursor: "pointer" }}>
            {value?.length === 0 && (
              <span className="k-input-value-text" style={{ marginLeft: 10 }}>
                {placeholder}
              </span>
            )}
            <div
              className="k-chip-list k-chip-list-md"
              role="listbox"
              id={`tagslist-${id}`}
            >
              {visibleItems.map(item => {
                const chipLabelText = displayNameKey
                  ? item[displayNameKey as "name"]
                  : item[textField as "name"];
                const chipImage = imageRender?.(item);
                const defaultLabelElement = (
                  <span
                    aria-label={item[textField as "name"]}
                    className="k-chip-label"
                  >
                    {chipImage}
                    {chipImage != null ? " " : null}
                    {chipLabelText}
                  </span>
                );
                const labelContent = valueRender
                  ? valueRender(defaultLabelElement, item)
                  : defaultLabelElement;
                return (
                  <div
                    role="option"
                    dir="ltr"
                    key={item[dataItemKey as "id"] as string}
                    tabIndex={0}
                    className={`${isSingleSelect ? "k-chip-single-select" : "k-chip k-chip-md k-rounded-md k-chip-solid k-chip-solid-base"}`}
                    aria-disabled="false"
                    aria-keyshortcuts="Enter Delete"
                    style={{
                      maxWidth: rest === 0 ? "89%" : "85%",
                    }}
                  >
                    <span className="k-chip-content">
                      {labelContent}
                      {!isSingleSelect && (
                        <Button
                          fillMode="clear"
                          type="button"
                          aria-label={t("common.removeItemAriaLabel", {
                            name: String(chipLabelText),
                          })}
                          onClick={event => {
                            event.stopPropagation();
                            removeItem(item);
                          }}
                        >
                          <X size={16} aria-hidden="true" />
                        </Button>
                      )}
                    </span>
                  </div>
                );
              })}
              {rest > 0 && (
                <ToolTipContent
                  Content={() =>
                    value.map(item => {
                      const tooltipLabelText = displayNameKey
                        ? item[displayNameKey as "name"]
                        : item[textField as "name"];
                      const defaultTooltipElement = (
                        <span className="k-chip-label">{tooltipLabelText}</span>
                      );
                      const labelContent = valueRender
                        ? valueRender(defaultTooltipElement, item)
                        : defaultTooltipElement;
                      return (
                        <div
                          key={item[dataItemKey as "id"] as string}
                          style={{ display: "flex", gap: 5 }}
                        >
                          {imageRender?.(item)}
                          <div>{labelContent}</div>
                        </div>
                      );
                    })
                  }
                >
                  <div
                    role="option"
                    dir="ltr"
                    tabIndex={0}
                    className="k-chip k-chip-md k-rounded-md k-chip-solid k-chip-solid-base"
                    aria-disabled="false"
                    aria-keyshortcuts="More item"
                  >
                    +{rest}
                  </div>
                </ToolTipContent>
              )}
            </div>
          </div>
          {loading && (
            <span
              className="k-icon k-font-icon k-i-loading k-input-loading-icon"
              role="presentation"
            ></span>
          )}
          {!others.hideArrowIcon && (
            <button
              tabIndex={-1}
              type="button"
              aria-label="select"
              aria-hidden="true"
              className="k-button k-button-md k-button-solid k-button-solid-base k-icon-button k-input-button"
            >
              <span
                className="k-icon k-svg-icon k-svg-i-caret-alt-down k-button-icon"
                aria-hidden="true"
              >
                {svgIcon ? (
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox={svgIcon.viewBox}
                    dangerouslySetInnerHTML={{ __html: svgIcon.content }}
                  />
                ) : (
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                  >
                    <path d="M256 352 128 160h256z"></path>
                  </svg>
                )}
              </span>
            </button>
          )}
        </span>
        <Popup
          anchor={anchor.current}
          show={hidePopup ? false : show}
          popupClass={"k-list-container k-dropdownlist-popup"}
          animate={false}
          style={{ width: computePopupWidth }}
          className={classNames(
            "k-list-container k-dropdownlist-popup",
            `${className}--popup`,
            isSingleSelect && "tlx-single-select-popup",
          )}
        >
          <div ref={divRef}>
            <ListContainer
              label={label}
              value={value}
              onClearAll={onClearAll}
              onSelectAll={onSelectAll}
              onFilterChange={filterChange}
              filterValue={filterValue}
              id={id}
              dataItemKey={dataItemKey}
              textField={textField}
              data={groupTabView ? newData : dropDownData}
              onChange={onChange}
              imageRender={imageRender}
              itemRender={itemRender}
              isLoading={loading}
              onClose={() => setShow(false)}
              hasSelectAll={hasSelectAll ?? !hasAll}
              allowCustom={allowCustom}
              groupField={groupField}
              groupTabView={groupTabView}
              customValueRender={customValueRender}
              isSingleSelect={isSingleSelect}
              onLoadMore={onLoadMore}
              hasMore={hasMore}
              isLoadingMore={isLoadingMore}
            />
          </div>
        </Popup>
        {showHint && !hintIcon && <Hint id={hindId}>{hint}</Hint>}
        {showHint && hintIcon && <IconHint hint={hint} />}
        {showValidationMessage && (
          <Error id={errorId}>{validationMessage}</Error>
        )}
        {showApiError && <Errors errors={[apiError]} />}
      </FormLabel>
    </FieldWrapper>
  );
};

interface ListContainerProps {
  label?: string;
  value: SelectData[];
  onClearAll: () => void;
  onSelectAll: () => void;
  onClose: () => void;
  onFilterChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  filterValue: string;
  id: string;
  dataItemKey: string;
  textField: string;
  imageRender?(data: SelectData): React.ReactNode;
  customValueRender(
    filterValue: string,
    addCustomValue: (text: string) => void,
  ): React.ReactNode;
  data: SelectData[];
  onChange?: FieldRenderProps["onChange"];
  itemRender?: FieldRenderProps["itemRender"];
  hasSearch?: boolean;
  hasClear?: boolean;
  hasSelectAll: boolean;
  isLoading?: boolean;
  allowCustom?: boolean;
  // persistent custom items provided by parent (persist across open/close)
  customItems?: SelectData[];
  // callback to persist a newly created custom item into parent state
  onCreateCustom?: (item: SelectData) => void;
  groupField?: string;
  groupTabView?: boolean;
  isSingleSelect: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
}
export const ListContainer: React.FC<ListContainerProps> = props => {
  const {
    label,
    value,
    onClearAll,
    onFilterChange,
    filterValue,
    id,
    dataItemKey,
    textField,
    data,
    imageRender,
    itemRender,
    onChange,
    hasSearch = true,
    hasClear = true,
    onClose,
    isLoading = false,
    hasSelectAll = true,
    onSelectAll,
    allowCustom = false,
    customValueRender,
    isSingleSelect,
  } = props;

  // include groupField and groupTabView props
  const groupField = props.groupField as string | undefined;
  const groupTabView = props.groupTabView ?? false;

  // locally track custom items created by the user so they also show up in the dropdown
  const [customItems, setCustomItems] = useState<SelectData[]>([]);

  const mergedData = useMemo(() => {
    // Ensure we don't duplicate items by id
    const existingIds = new Set(
      (data || []).map(d => d[dataItemKey as keyof SelectData]),
    );

    const filteredCustom = (customItems || []).filter(
      ci => !existingIds.has(ci[dataItemKey as keyof SelectData]),
    );

    // Build a set of ids that are already present (data + filteredCustom)
    const getIdValue = (
      item: SelectData,
    ): string | number | string[] | undefined => {
      const value = item[dataItemKey as keyof SelectData];
      return typeof value === "boolean" ? undefined : value;
    };

    const presentIds = new Set<string | number | string[] | undefined>([
      ...Array.from(existingIds).filter(
        (v): v is string | number | string[] | undefined =>
          typeof v !== "boolean",
      ),
      ...(filteredCustom || []).map(getIdValue),
    ]);

    // Include any selected values that are not present in data/customItems so custom selected items persist
    const selectedValues = filterValue?.length
      ? []
      : (value || []).filter(sv => {
          const idValue = getIdValue(sv);
          return idValue !== undefined && !presentIds.has(idValue);
        });

    return [...(data || []), ...filteredCustom, ...selectedValues];
  }, [data, customItems, value, dataItemKey, filterValue]);

  const groupedDataLocal = useMemo(() => {
    if (!groupField) return [{ group: null, items: mergedData }];
    const groups = new Map<string | null, SelectData[]>();
    // use mergedData (includes selected values) for grouping
    mergedData.forEach(item => {
      const group = item[groupField as keyof SelectData] || null;
      if (!groups.has(group as string)) groups.set(group as string, []);
      groups.get(group as string)?.push(item);
    });

    return Array.from(groups.entries()).map(([group, items]) => ({
      group,
      items,
    }));
  }, [mergedData, groupField]);

  // Determine whether to show the "Add ..." row: show when `allowCustom` is enabled,
  // the user has typed a non-empty filterValue, and there's no exact (case-insensitive)
  // match in mergedData for that typed value.
  const canShowAddRow = useMemo(() => {
    if (!allowCustom || !filterValue?.trim()) return false;
    const normalized = filterValue.trim().toLowerCase();
    return !mergedData.some(d => {
      const v = d[textField as keyof SelectData] as unknown as string;
      return (v || "").toLowerCase() === normalized;
    });
  }, [allowCustom, filterValue, mergedData, textField]);

  const addCustomValue = useCallback(
    (addItem: string | SelectData, target?: EventTarget | null) => {
      if (!addItem) return;
      // normalize and check for existing item by textField (case-insensitive)
      const normalized = isString(addItem)
        ? addItem.trim().toLowerCase()
        : addItem[textField as keyof SelectData]?.toString().toLowerCase();
      const existing = mergedData.find(d => {
        const v = d[textField as keyof SelectData] as unknown as string;
        return v?.toLowerCase() === normalized;
      });

      if (existing) {
        // select existing item
        onChange?.({ target: target, value: [...(value || []), existing] });
        onFilterChange?.({
          target: { value: "" },
        } as unknown as React.ChangeEvent<HTMLInputElement>);
        return;
      }

      let newItem: SelectData & { __isCustom?: boolean };
      // dataItemKey is assumed to be unique identifier, so we can use the text as id for custom items
      if (isString(addItem)) {
        newItem = {
          name: addItem,
          [dataItemKey]: addItem,
          [textField]: addItem,
        } as SelectData & { __isCustom?: boolean };
      } else {
        newItem = addItem;
      }
      // mark as custom (optional) so callers can detect these
      newItem.__isCustom = true;

      if (isSingleSelect) {
        setCustomItems([newItem]);
        onChange?.({ target: target, value: [newItem] });
        onClose();
      } else {
        setCustomItems(prev => [...prev, newItem]);
        onChange?.({ target: target, value: [...(value || []), newItem] });
      }
      // Clear filter input via calling parent's onFilterChange
      onFilterChange?.({
        target: { value: "" },
      } as unknown as React.ChangeEvent<HTMLInputElement>);
    },
    [
      mergedData,
      textField,
      onChange,
      value,
      onFilterChange,
      dataItemKey,
      isSingleSelect,
      onClose,
    ],
  );

  const computeCustomValueRender = useMemo(() => {
    return customValueRender(filterValue, addCustomValue);
  }, [customValueRender, filterValue, addCustomValue]);

  // Determine what actions should be shown
  const showSelectAllClearAll = hasClear && hasSelectAll && !isSingleSelect;
  const showClearAllOnly = hasClear && !hasSelectAll && value?.length > 0;
  const showActions = showSelectAllClearAll || showClearAllOnly;
  const hasContent = !!label || showActions;

  return (
    <>
      <div
        className="k-d-flex k-justify-content-between"
        style={{
          height: hasContent ? 35 : 0,
          alignItems: "center",
        }}
      >
        <If condition={label}>
          <Then>
            <span
              className="k-font-bold"
              style={{ padding: "var(--tlx-space-1) var(--tlx-space-2)" }}
            >
              {label}
            </span>
          </Then>
        </If>
        <If condition={showSelectAllClearAll}>
          <Then>
            <span
              onClick={isEmpty(value) ? onSelectAll : onClearAll}
              className="k-cursor-pointer k-font-medium k-text-tertiary"
              style={{ padding: 5 }}
            >
              {isEmpty(value) ? "Select All" : `Clear All (${value?.length})`}
            </span>
          </Then>
        </If>
        <If condition={showClearAllOnly}>
          <Then>
            <span
              onClick={onClearAll}
              className="k-cursor-pointer k-font-medium k-text-tertiary"
              style={{ padding: 5 }}
            >{`Clear All (${value?.length})`}</span>
          </Then>
        </If>
      </div>
      <If condition={hasSearch && !groupTabView}>
        <Then>
          <div className="k-list-filter">
            <span
              className="k-textbox k-input k-input-md k-input-solid k-rounded-md "
              onKeyDown={ev => ev.stopPropagation()}
            >
              <span className="k-input-prefix k-input-prefix-horizontal">
                <span
                  className="k-icon k-svg-icon k-svg-i-search"
                  aria-hidden="true"
                >
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                  >
                    <path d="M365.3 320h-22.7l-26.7-26.7C338.5 265.7 352 230.4 352 192c0-88.4-71.6-160-160-160S32 103.6 32 192s71.6 160 160 160c38.4 0 73.7-13.5 101.3-36.1l26.7 26.7v22.7L434.7 480l45.3-45.3zM64 192c0-70.7 57.3-128 128-128s128 57.3 128 128-57.3 128-128 128S64 262.7 64 192"></path>
                  </svg>
                </span>
              </span>
              <input
                className="k-input-inner"
                value={filterValue}
                onChange={onFilterChange}
                placeholder={
                  allowCustom ? "Enter a value to search or add" : "Search..."
                }
                onKeyDown={ev => {
                  // Prevent the popup from closing and stop propagation
                  ev.stopPropagation();
                }}
              />
            </span>
          </div>
        </Then>
      </If>
      {/*
        Show "Add ..." row when the user has typed something and there is no exact
        match (case-insensitive) in mergedData. This allows creating multiple
        distinct custom items even if mergedData already contains other items.
      */}
      {isLoading && (
        <NoDataPopup isLoading={isLoading} allowCustom={allowCustom} />
      )}

      {!isLoading && canShowAddRow && !groupTabView && (
        <div className="k-list-container k-multiselect-popup">
          <div className="k-list k-list-md">
            <ul className="k-list-ul" style={{ padding: 0 }}>
              {computeCustomValueRender}
            </ul>
          </div>
        </div>
      )}

      {!isLoading && !canShowAddRow && mergedData.length === 0 && (
        <NoDataPopup isLoading={isLoading} allowCustom={allowCustom} />
      )}
      <If condition={!isLoading && mergedData.length > 0}>
        <Then>
          <List
            groupedData={groupedDataLocal}
            groupField={groupField}
            groupTabView={groupTabView}
            data={mergedData}
            id={id}
            onChange={onChange}
            values={value || []}
            imageRender={imageRender}
            itemRender={itemRender}
            dataItemKey={dataItemKey}
            textField={textField}
            isSingleSelect={isSingleSelect}
            onClose={onClose}
            allowCustom={allowCustom}
            addCustomValue={addCustomValue}
            customValueRender={customValueRender}
            onFilterChange={onFilterChange}
            onLoadMore={props.onLoadMore}
            hasMore={props.hasMore}
            isLoadingMore={props.isLoadingMore}
          />
        </Then>
      </If>

      <If condition={!isSingleSelect}>
        <Then>
          <div
            className="k-listview-footer"
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "var(--tlx-space-4)",
              borderTop: "1px solid var(--tlx-stroke-border-default)",
            }}
          >
            <Button
              onClick={onClose}
              fillMode="outline"
              size="small"
              style={{
                padding: "var(--tlx-space-1) var(--tlx-space-2)",
              }}
            >
              Close
            </Button>
          </div>
        </Then>
      </If>
    </>
  );
};

const spacing = 45;
const spacingWithImageRender = 60;

const List: React.FC<{
  groupedData: { group: string | null; items: SelectData[] }[];
  groupField?: string;
  groupTabView?: boolean;
  data: SelectData[];
  id: string;
  onChange?: FieldRenderPropsBase["onChange"];
  itemRender?: (
    span: React.ReactElement<HTMLSpanElement>,
    itemProps: {
      dataItem: SelectData;
      textField: string;
      dataItemKey: string;
      ulWidth: number | undefined;
      spacing: number;
    },
  ) => React.ReactNode;
  values: SelectData[];
  dataItemKey: string;
  textField: string;
  imageRender?(data: SelectData): React.ReactNode;
  isSingleSelect: boolean;
  onClose: () => void;
  allowCustom?: boolean;
  addCustomValue?: (
    addItem: string | SelectData,
    target?: EventTarget | null,
  ) => void;
  customValueRender?: (
    filterValue: string,
    addCustomValue: (text: string) => void,
  ) => React.ReactNode;
  onFilterChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Called when user scrolls within 50px of the bottom and more pages exist. */
  onLoadMore?: () => void;
  /** Activates the scroll listener when true. Safe to omit — defaults to false (no listener). */
  hasMore?: boolean;
  /** True while a next-page fetch is in flight; prevents duplicate triggers. */
  isLoadingMore?: boolean;
}> = ({
  groupedData,
  groupField,
  groupTabView = false,
  id,
  itemRender,
  onChange,
  values = [],
  imageRender,
  dataItemKey,
  textField,
  isSingleSelect,
  onClose,
  allowCustom = false,
  addCustomValue,
  customValueRender,
  onFilterChange,
  onLoadMore,
  hasMore,
  isLoadingMore,
}) => {
  const [, setSelected] = useState<SelectData[]>(values);
  const [ulWidth, setUlWidth] = useState<number>();
  const [selectedTab, setSelectedTab] = useState(0);
  const [filterByTab, setFilterByTab] = useState<Record<number, string>>({});
  const anchor = React.useRef<HTMLUListElement | null>(null);
  const listContentRef = React.useRef<HTMLDivElement | null>(null);

  useInfiniteScroll({
    containerRef: listContentRef,
    hasMore: hasMore ?? false,
    isLoading: isLoadingMore ?? false,
    onLoadMore: onLoadMore ?? noop,
    threshold: 50,
  });

  const tabFilterValue = filterByTab[selectedTab] ?? "";

  useDeepCompareEffect(() => {
    setSelected(values);
  }, [values]);

  const onClick = useCallback(
    (target: EventTarget, value: SelectData) => {
      setSelected(prevState => {
        if (isSingleSelect) {
          onChange?.({ target, value: [value] });
          onClose();
          return [value];
        }
        const state = prevState || [];
        const isExist = state.find(
          item => item[dataItemKey as "id"] === value[dataItemKey as "id"],
        );
        const changeItems = isExist
          ? state.filter(
              item => item[dataItemKey as "id"] !== value[dataItemKey as "id"],
            )
          : [...state, value];
        onChange?.({ target, value: changeItems });
        return changeItems;
      });
    },
    [isSingleSelect, onClose, onChange, dataItemKey],
  );

  useEffect(() => {
    setUlWidth(anchor.current?.getBoundingClientRect().width);
  }, [anchor]);

  useEffect(() => {
    if (groupTabView && anchor.current) {
      setUlWidth(anchor.current.getBoundingClientRect().width);
    }
  }, [groupTabView, selectedTab]);

  const renderGroupItems = useCallback(
    (items: SelectData[], groupIndex: number) =>
      items.map((item, index) => {
        const itemId = item[dataItemKey as "id"] as string;
        const checked = !!values?.find(
          value => value[dataItemKey as "id"] === itemId,
        );
        const disabled = item.disabled || item.checkAllDisabled || false;

        return (
          <li
            key={`${itemId}-${groupIndex}-${index}`}
            id={`option-${id}-guid-${groupIndex}-${index}`}
            role="option"
            aria-selected={checked ? "true" : "false"}
            className={classNames("k-list-item", {
              "k-disabled": disabled,
            })}
            style={{ position: "unset" }}
            onClick={({ target }) => !disabled && onClick(target, item)}
          >
            <div
              style={
                !isSingleSelect || imageRender
                  ? { display: "flex", gap: 5 }
                  : { width: "100%" }
              }
            >
              {!isSingleSelect && (
                <Checkbox checked={checked} disabled={disabled} />
              )}
              <If condition={isFunction(itemRender)}>
                <Then>
                  {itemRender?.(<span />, {
                    dataItem: item,
                    textField,
                    dataItemKey,
                    ulWidth,
                    spacing,
                  })}
                </Then>
                <Else>
                  {imageRender?.(item)}
                  <Tooltip
                    anchorElement="target"
                    position="top"
                    filter={TitleFilter}
                  >
                    <div
                      title={item[textField as "name"]}
                      style={{
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        width: ulWidth
                          ? ulWidth -
                            (imageRender ? spacingWithImageRender : spacing)
                          : undefined,
                      }}
                    >
                      {item[textField as "name"]}
                    </div>
                  </Tooltip>
                </Else>
              </If>
            </div>
          </li>
        );
      }),
    [
      id,
      values,
      ulWidth,
      itemRender,
      imageRender,
      dataItemKey,
      textField,
      isSingleSelect,
      onClick,
    ],
  );

  const renderItems = useMemo(() => {
    return groupedData.map(({ group, items }, groupIndex) => (
      <React.Fragment key={`${group}-${groupIndex}`}>
        {groupField && group && (
          <li className="k-list-group-header sticky-header k-font-bold">
            {group}
          </li>
        )}
        {renderGroupItems(items, groupIndex)}
      </React.Fragment>
    ));
  }, [groupedData, groupField, renderGroupItems]);

  const showTabView =
    groupTabView &&
    groupField &&
    groupedData.length > 1 &&
    groupedData.some(g => g.group != null);

  const filteredItemsForTab = useMemo(() => {
    if (!showTabView || !groupedData[selectedTab]) return [];
    const items = groupedData[selectedTab].items ?? [];
    if (!tabFilterValue?.trim()) return items;
    return filterBy([...items], {
      logic: "or",
      filters: [
        {
          field: textField,
          operator: "contains",
          value: tabFilterValue,
        },
      ],
    });
  }, [showTabView, groupedData, selectedTab, tabFilterValue, textField]);

  const canShowAddRowInTab = useMemo(() => {
    if (!allowCustom || !tabFilterValue?.trim()) return false;
    const normalized = tabFilterValue.trim().toLowerCase();
    const tabItems = groupedData[selectedTab]?.items ?? [];
    return !tabItems.some(d => {
      const v = d[textField as keyof SelectData] as unknown as string;
      return (v || "").toLowerCase() === normalized;
    });
  }, [allowCustom, tabFilterValue, groupedData, selectedTab, textField]);

  const handleTabFilterChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value ?? "";
      setFilterByTab(prev => ({ ...prev, [selectedTab]: value }));
      onFilterChange?.(event);
    },
    [selectedTab, onFilterChange],
  );

  const wrappedAddCustomValue = useCallback(
    (addItem: string | SelectData, target?: EventTarget | null) => {
      addCustomValue?.(addItem, target);
      setFilterByTab(prev => ({ ...prev, [selectedTab]: "" }));
    },
    [addCustomValue, selectedTab],
  );

  const tabAddCustomRender = useMemo(() => {
    if (!customValueRender || !canShowAddRowInTab) return null;
    return customValueRender(tabFilterValue, text =>
      wrappedAddCustomValue(text),
    );
  }, [
    customValueRender,
    canShowAddRowInTab,
    tabFilterValue,
    wrappedAddCustomValue,
  ]);

  const listContent = (
    <ul
      id={`${id}-listbox-id`}
      role="listbox"
      className="k-list-ul"
      ref={anchor}
    >
      {showTabView
        ? renderGroupItems(filteredItemsForTab, selectedTab)
        : renderItems}
    </ul>
  );

  const tabSearchInput = (
    <div className="k-list-filter">
      <span
        className="k-textbox k-input k-input-md k-input-solid k-rounded-md"
        onKeyDown={ev => ev.stopPropagation()}
      >
        <span className="k-input-prefix k-input-prefix-horizontal">
          <span className="k-icon k-svg-icon k-svg-i-search" aria-hidden="true">
            <svg
              aria-hidden="true"
              focusable="false"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
            >
              <path d="M365.3 320h-22.7l-26.7-26.7C338.5 265.7 352 230.4 352 192c0-88.4-71.6-160-160-160S32 103.6 32 192s71.6 160 160 160c38.4 0 73.7-13.5 101.3-36.1l26.7 26.7v22.7L434.7 480l45.3-45.3zM64 192c0-70.7 57.3-128 128-128s128 57.3 128 128-57.3 128-128 128S64 262.7 64 192"></path>
            </svg>
          </span>
        </span>
        <input
          className="k-input-inner"
          value={tabFilterValue}
          onChange={handleTabFilterChange}
          placeholder={
            allowCustom ? "Enter a value to search or add" : "Search..."
          }
          onKeyDown={ev => ev.stopPropagation()}
        />
      </span>
    </div>
  );

  if (showTabView) {
    return (
      <div className="tlx-list tlx-multiselect-group-tabs k-list k-list-md">
        <TabStrip
          selected={selectedTab}
          onSelect={e => setSelectedTab(e.selected)}
          style={{ flex: 1 }}
        >
          {groupedData.map(({ group }, idx) => (
            <TabStripTab key={group ?? idx} title={group ?? "All"}>
              {selectedTab === idx && (
                <>
                  {tabSearchInput}
                  {canShowAddRowInTab && tabAddCustomRender && (
                    <div className="k-list-container k-multiselect-popup">
                      <div className="k-list k-list-md">
                        <ul className="k-list-ul" style={{ padding: 0 }}>
                          {tabAddCustomRender}
                        </ul>
                      </div>
                    </div>
                  )}
                  <div className="k-list-content">{listContent}</div>
                </>
              )}
            </TabStripTab>
          ))}
        </TabStrip>
      </div>
    );
  }

  return (
    <div className="tlx-list k-list k-list-md">
      <div className="k-list-content" ref={listContentRef}>
        {listContent}
      </div>
    </div>
  );
};

const NoDataPopup: React.FC<{ allowCustom: boolean; isLoading?: boolean }> = ({
  isLoading,
  allowCustom,
}) => {
  return (
    <div className="k-list-container k-multiselect-popup">
      <div className="k-list k-list-md">
        <div className="k-nodata">
          <div style={{ minWidth: "9.375rem" }}>
            {!isLoading && (
              <h4 style={{ fontSize: "1em" }}>
                <span className="k-icon k-font-icon k-i-warning"></span>
              </h4>
            )}
            {isLoading ? "Loading..." : "No Data Found"}
            {!isLoading && allowCustom && (
              <p style={{ color: "var(--tlx-color-dark)", fontWeight: 300 }}>
                Enter a new value in the search box to add{" "}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const TitleFilter = (element: HTMLElement) => {
  const isEllipsisActive = element.offsetWidth < element.scrollWidth;

  if (isEllipsisActive) return true;
  else {
    element.title = "";
    return false;
  }
};

export default MultiSelect;
